'use client'

import { useState } from 'react'

export type ReaderSettings = {
  textSize: number
  lineSpacing: number
  script: 'simplified' | 'traditional'
  showPinyin: boolean
  showZhuyin: boolean
  showTranslation: boolean
}

const STORAGE_KEY = 'miaozi-reader-settings'

export const DEFAULT_READER_SETTINGS: ReaderSettings = {
  textSize: 22,
  lineSpacing: 1.75,
  script: 'simplified',
  showPinyin: true,
  showZhuyin: false,
  showTranslation: true
}

function sanitizeStoredSettings(value: unknown): ReaderSettings {
  if (!value || typeof value !== 'object') return DEFAULT_READER_SETTINGS

  const stored = value as Partial<ReaderSettings>
  const textSize = Number(stored.textSize)
  const lineSpacing = Number(stored.lineSpacing)
  const script =
    stored.script === 'traditional' || stored.script === 'simplified'
      ? stored.script
      : DEFAULT_READER_SETTINGS.script

  return {
    textSize:
      Number.isFinite(textSize) && textSize >= 18 && textSize <= 30
        ? textSize
        : DEFAULT_READER_SETTINGS.textSize,
    lineSpacing:
      Number.isFinite(lineSpacing) && lineSpacing >= 1.35 && lineSpacing <= 2.2
        ? lineSpacing
        : DEFAULT_READER_SETTINGS.lineSpacing,
    script,
    showPinyin:
      typeof stored.showPinyin === 'boolean'
        ? stored.showPinyin
        : DEFAULT_READER_SETTINGS.showPinyin,
    showZhuyin:
      typeof stored.showZhuyin === 'boolean'
        ? stored.showZhuyin
        : DEFAULT_READER_SETTINGS.showZhuyin,
    showTranslation:
      typeof stored.showTranslation === 'boolean'
        ? stored.showTranslation
        : DEFAULT_READER_SETTINGS.showTranslation
  }
}

function readStoredSettings() {
  if (typeof window === 'undefined') return DEFAULT_READER_SETTINGS

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_READER_SETTINGS
    return sanitizeStoredSettings(JSON.parse(raw))
  } catch {
    return DEFAULT_READER_SETTINGS
  }
}

function saveStoredSettings(settings: ReaderSettings) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // localStorage can fail in private browsing. Reader settings still work in memory.
  }
}

export function useReaderSettings() {
  const [settings, setSettingsState] =
    useState<ReaderSettings>(readStoredSettings)

  const setSettings = (updates: Partial<ReaderSettings>) => {
    setSettingsState((previous) => {
      const next = { ...previous, ...updates }
      saveStoredSettings(next)
      return next
    })
  }

  return {
    settings,
    hydrated: true,
    setSettings
  }
}
